import { useState, useRef } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { deleteAccount } from '../api'
import { useAuth } from '../../auth'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tendbot/ui'

type ModalState = 'idle' | 'loading' | 'error'

interface Props {
  open: boolean
  onClose: () => void
}

export default function DeleteAccountModal({ open, onClose }: Props) {
  const { t } = useTranslation('settings')
  const { signOut } = useAuth()
  const [state, setState] = useState<ModalState>('idle')
  const [confirmation, setConfirmation] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      onClose()
      return
    }
    setState('idle')
    setConfirmation('')
  }

  const canDelete = confirmation.toLowerCase() === 'delete'

  const handleDelete = async () => {
    if (!canDelete) return
    setState('loading')
    try {
      await deleteAccount()
      await signOut()
    } catch {
      setState('error')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        accent="rose"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-rose/10 border border-rose/20 grid place-items-center">
            <AlertTriangle size={28} className="text-rose" />
          </div>
        </div>
        <DialogHeader>
          <DialogTitle>{t('deleteAccount.title')}</DialogTitle>
          <DialogDescription>{t('deleteAccount.description')}</DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="delete-confirmation" className="text-sm font-medium text-cream">
              {t('deleteAccount.confirmLabel')}
            </label>
            <input
              ref={inputRef}
              id="delete-confirmation"
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder={t('deleteAccount.confirmPlaceholder')}
              autoComplete="off"
              className="px-4 py-3 bg-midnight/60 border border-slate-mid rounded-lg font-body text-base text-ivory placeholder:text-pearl/45 focus:outline-none focus:border-rose/40 focus:ring-2 focus:ring-rose/10 transition-colors"
            />
          </div>
          {state === 'error' && (
            <p className="text-sm text-rose text-center">{t('deleteAccount.error')}</p>
          )}
        </div>
        <DialogFooter className="mt-6 gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl bg-slate-mid/50 border border-slate-mid text-pearl font-medium text-sm hover:bg-slate-mid/80 transition-colors"
          >
            {t('deleteAccount.cancel')}
          </button>
          <button
            onClick={handleDelete}
            disabled={!canDelete || state === 'loading'}
            className="flex-1 py-3 rounded-xl bg-rose/80 text-white font-semibold text-sm hover:bg-rose transition-colors disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center gap-2"
          >
            {state === 'loading' ? (
              <>
                <span className="w-4 h-4 border-2 border-transparent border-t-white rounded-full animate-spin" />
                {t('deleteAccount.deleting')}
              </>
            ) : (
              t('deleteAccount.confirm')
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
